import { TextWithDropShadow } from '../TextWithDropShadow';
import { twMerge } from 'tailwind-merge';
import clsx, { ClassValue } from 'clsx';
import { useState } from 'react';
import IconComponent from './Icon';
import Image from 'next/image';

export interface StrapiFlashCardData {
  id: number;
  body: string;
  hidden: boolean;
  icon: {
    name: string;
  };
  iconBGColor: string;
  iconTextColor: string;
  iconImage: string;
  whiteText: boolean;
  title: string;
}

export const flashCardBGColors = [
  'bg-[#CFECFF]',
  'bg-[#F2DECF]',
  'bg-[#F8DDDD]',
  'bg-[#E6E2F3]',
  'bg-[#DDECD4]',
  'bg-[#F0F4F5]',
  'bg-[#00263E]',
  'bg-[#FEE8C6]',
  'bg-[#FCF2C5]',
];

const cn = (...classes: ClassValue[]) => twMerge(clsx(classes));

export default function FlashCard({ data }: { data: StrapiFlashCardData }) {
  const [cardState, setCardState] = useState({
    showBack: false,
    flip() {
      setCardState((prevState) => {
        return {
          ...prevState,
          showBack: !prevState.showBack,
        };
      });
    },
  });
  const [buttonState, setButtonState] = useState({
    highlight: false,
    flip() {
      setButtonState((prevState) => {
        return {
          ...prevState,
          highlight: !prevState.highlight,
        };
      });
    },
  });

  return (
    <>
      <button
        type='button'
        onClick={() => cardState.flip()}
        className='h-[256px] w-[320px] outline-none [perspective:100rem]'
        onMouseOver={() => buttonState.flip()}
        onMouseOut={() => buttonState.flip()}
      >
        <div
          className={cn(
            'relative size-full transition duration-1000 [transform-style:preserve-3d]',
            cardState.showBack && '[transform:rotateY(180deg)]'
          )}
        >
          <div className='absolute size-full [backface-visibility:hidden]'>
            <div className='flex h-full w-full flex-col rounded-2xl border-2 border-gray-300 p-9 shadow-lg'>
              <div
                className={twMerge(
                  'mx-auto mb-4 flex size-[60px] items-center justify-center rounded-full ',
                  `bg-[${data.iconBGColor}]`
                )}
              >
                {data.iconImage ? (
                  <Image
                    src={data.iconImage}
                    alt='Card Icon'
                    width={36}
                    height={36}
                  />
                ) : (
                  <IconComponent
                    name={data.icon?.name}
                    className='size-9'
                    fill={data.iconTextColor}
                  />
                )}
              </div>
              <div
                className={cn(
                  'text-2xl font-medium transition duration-500',
                  cardState.showBack ? 'opacity-0 delay-300' : 'opacity-100'
                )}
              >
                <TextWithDropShadow text={data.title} displayShadow={false} />
              </div>
              <div
                className={twMerge(
                  'mx-auto mt-4 max-w-fit rounded-full border border-gray-300 px-4 py-1 text-sm',
                  buttonState.highlight
                    ? 'bg-[#0066A2] text-white'
                    : 'bg-white text-gray-600'
                )}
              >
                Learn More
              </div>
            </div>
          </div>
          <div className='absolute inset-0 size-full [backface-visibility:hidden] [transform:rotateY(180deg)]'>
            <div
              className={twMerge(
                'border-2 border-gray-300 shadow-lg',
                'flex h-full w-full flex-col items-center justify-center rounded-2xl p-6',
                `text-[${data.iconTextColor}]`,
                `bg-[${data.iconBGColor}]`
              )}
            >
              <div
                className={twMerge(
                  'mx-auto mb-4 flex size-[60px] items-center justify-center rounded-full ',
                  `bg-white`
                )}
              >
                {data.iconImage ? (
                  <Image
                    src={data.iconImage}
                    alt='Card Icon'
                    width={36}
                    height={36}
                  />
                ) : (
                  <IconComponent
                    name={data.icon?.name}
                    className='size-9'
                    fill={data.iconBGColor}
                  />
                )}
              </div>
              <div className='block'>
                <TextWithDropShadow
                  text={data.body}
                  color={{
                    text: data.whiteText ? 'text-white' : 'text-black',
                    shadow: 'text-white',
                  }}
                  displayShadow={false}
                />
              </div>
            </div>
          </div>
        </div>
      </button>
    </>
  );
}
